'use strict';

define('vb/action/builtin/openUrlAction',[
  'vb/action/action',
  'vb/private/stateManagement/router',
],
(Action, Router) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  /**
   * Open the specified resource in the current window or in a new window using the window.open() API
   * as defined at {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/open|Window.open()}
   *
   * @example
   * // An example of entry to open the URL in a new window as seen in a page descriptor:
   * "parameters": {
   *   "url": "http://mysite/index.html",
   *   "params": {
   *     "id": "{{ $variables.id }}",
   *   },
   *   "hash": "details",
   *   "name": "myOtherWindow",
   * }
   *
   * @extends Action
   * @category Action
   */
  class OpenUrlAction extends Action {
    /**
     * Performs the action of opening a URL based on the provided parameters.
     *
     * @param {Object} parameters
     * @param {String} parameters.url The url to navigate to
     * @param {Object} [parameters.params] An object with each property is a URL parameters
     * @param {String} [parameters.hash] The hash entry
     * @param {String} [parameters.history] Effect on the browser history
     * This is only used when opening the resource in the same window.
     * Allowed values are 'replace' or 'push'. Default is 'push'.
     * If the value is 'replace', the current browser history entry is
     * replaced instead of pushed, meaning that the browser back button
     *  will not go back to it.
     * @param {String} [parameters.windowName] A name identifying the window as defined in window.open API.
     * If not defined, the URL will be open in the current window otherwise refer to the window.open API.
     *
     * @returns {Promise<Outcome>} A promise that resolves with an outcome object indicating success or failure.
     *                           The outcome object has a 'success' property set to true if the operation was
     *                           successful, or a 'failure' property with an error message if the operation failed.
     */
    // eslint-disable-next-line class-methods-use-this
    perform(parameters) {
      return Router.openUrl(parameters)
        .then(() => Action.createSuccessOutcome())
        .catch((e) => Action.createFailureOutcome('Failed to open url', e));
    }
  }

  return OpenUrlAction;
});

